import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { NavLink } from "react-router-dom";
import SuccessButton from "../../components/shared/SuccessButton";
import ErrorBlock from "../../components/shared/ErrorBlock";
import {
   fetchAccountSettings,
   fetchProducts,
   fetchPickupMoments,
} from "../../util/admin/httpAdmin";
import DashboardTopBarAdmin from "../../components/admin/DashboardTopBarAdmin";
import HomeBlueIcon from "../../assets/admin/icons/home-blue.svg";
import editIconWhite from "../../assets/admin/icons/edit-white.svg";
import AddSubdomainFormAdmin from "../../components/admin/home/AddSubdomainFormAdmin";
import OnboardingButton from "../../components/admin/home/OnboardingButton";
import LoadingIndicatorButton from "../../components/shared/LoadingIndicatorButton";
import HomeDashboardStepsCompletedAdmin from "../../components/admin/home/HomeDashboardStepsCompletedAdmin";

function HomeDashboardAdmin({ restoOwner }) {
   const queryClient = useQueryClient();
   const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(false);

   const {
      data: accountData,
      isLoading: accountLoading,
      isError: accountError,
      refetch: refetchAccountData,
   } = useQuery({
      queryKey: ["accountSettings"],
      queryFn: fetchAccountSettings,
   });

   const { data: products, isLoading: productsLoading } = useQuery({
      queryKey: ["adminProducts"],
      queryFn: fetchProducts,
   });

   const {
      data: pickupData,
      isLoading: loadingPickups,
      isError: errorPickups,
   } = useQuery({
      queryKey: ["pickupMoments"],
      queryFn: fetchPickupMoments,
   });

   // Check onboarding status
   useEffect(() => {
      if (
         localStorage.getItem("onBoardingInitiated") === "true" &&
         accountData &&
         !accountData.onboardingCompleted
      ) {
         setIsCheckingOnboarding(true);
         const timer = setTimeout(() => {
            refetchAccountData(); // Fetch account data opnieuw
            setIsCheckingOnboarding(false);
         }, 7000);

         return () => clearTimeout(timer);
      }
   }, [accountData, refetchAccountData]);

   const steps = [];

   if (
      !accountLoading &&
      (!accountData ||
         !accountData.shopName ||
         !accountData.shopDescription ||
         !accountData.shopAddress)
   ) {
      steps.push(
         <ErrorBlock
            key="account-settings"
            title="Instellingen Ontbreken"
            message="Je moet je accountgegevens aanvullen (naam, beschrijving, adres) voordat je kan lanceren."
         >
            <NavLink to="/dashboard/instellingen?edit=true">
               <SuccessButton>
                  <div className="g-15-1fr f-s">
                     <img src={editIconWhite} alt="bewerken" />
                     <span>Bewerken</span>
                  </div>
               </SuccessButton>
            </NavLink>
         </ErrorBlock>
      );
   }

   if (!productsLoading && (!products || products.length === 0)) {
      steps.push(
         <ErrorBlock
            key="products"
            title="Geen Producten"
            message="Je moet minstens één product aanmaken voordat je je webshop kan lanceren."
         >
            <NavLink to="/dashboard/producten?add=true">
               <SuccessButton>
                  <div className="g-15-1fr f-s">
                     <img src={editIconWhite} alt="bewerken" />
                     <span>Bewerken</span>
                  </div>
               </SuccessButton>
            </NavLink>
         </ErrorBlock>
      );
   }

   if (
      !loadingPickups &&
      (!pickupData ||
         ![
            "maandag",
            "dinsdag",
            "woensdag",
            "donderdag",
            "vrijdag",
            "zaterdag",
            "zondag",
         ].some((day) => pickupData[day] && pickupData[day].length > 0))
   ) {
      steps.push(
         <ErrorBlock
            key="pickup-moments"
            title="Ophaalmomenten Ontbreken"
            message="Je moet minstens één ophaalmoment instellen voordat je kan lanceren."
         >
            <NavLink to="/dashboard/ophaalmomenten">
               <SuccessButton>
                  <div className="g-15-1fr f-s">
                     <img src={editIconWhite} alt="bewerken" />
                     <span>Bewerken</span>
                  </div>
               </SuccessButton>
            </NavLink>
         </ErrorBlock>
      );
   }

   return (
      <>
         <DashboardTopBarAdmin
            title="Home"
            icon={HomeBlueIcon}
            description="Bekijk hier de informatie over jouw winkel"
         />
         <div className="py-20 px-20 g-20">
            {accountLoading || productsLoading || loadingPickups ? (
               <LoadingIndicatorButton />
            ) : isCheckingOnboarding ? (
       
                  <h2 className="flex-1">We kijken na of je alle stappen hebt voltooid <LoadingIndicatorButton dark/></h2>
        
            ) : steps.length > 0 ? (
               <div className="g-20">
                  <h2>Voltooi de volgende stappen om je webshop te lanceren</h2>
                  {steps}
               </div>
            ) : accountData.onboardingCompleted ? (
               <HomeDashboardStepsCompletedAdmin subdomain={accountData.subdomain}></HomeDashboardStepsCompletedAdmin>
            ) : !accountData.subdomain ? (
               <AddSubdomainFormAdmin />
            ) : (
               <OnboardingButton
                  subdomain={accountData.subdomain}
               ></OnboardingButton>
            )}
         </div>
      </>
   );
}

export default HomeDashboardAdmin;
